import { type TimeSlotWithExtras } from "@src/appV2/Shifts/types";
import { addMinutes } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- @clipboard-health/date-time doesn't export zonedTimeToUtc equivalent
import { zonedTimeToUtc } from "date-fns-tz";
import { useMemo, useRef } from "react";

import { type ShiftTimeDefinition, useGetShiftTimes } from "../../../api/useGetShiftTimes";

interface UseDefaultShiftTimesOptions {
  workplaceId: string;
  qualification: string;
  timeSlot: TimeSlotWithExtras;
  selectedDate: Date;
  timezone: string;
  /** When false, skips the internal API query. Defaults to true. */
  enabled?: boolean;
}

interface DefaultTimes {
  start: Date;
  end: Date;
}

interface UseDefaultShiftTimesReturn {
  defaultTimes: DefaultTimes | undefined;
  isLoading: boolean;
  error: Error | undefined;
  isQualificationSpecificEnabled: boolean;
}

/**
 * Converts shift time definition to Date objects representing the correct moment in time.
 * The hours/minutes from the API represent workplace local time, so we use zonedTimeToUtc
 * to create Date objects that represent that exact moment in UTC.
 */
function convertToDateTimes(
  shiftTime: ShiftTimeDefinition,
  selectedDate: Date,
  timezone: string
): DefaultTimes {
  // Extract the calendar date from selectedDate (in browser timezone)
  const year = selectedDate.getFullYear();
  const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
  const day = String(selectedDate.getDate()).padStart(2, "0");

  // Format the start time from API (which represents workplace local time)
  const startHour = String(shiftTime.start.hour).padStart(2, "0");
  const startMinute = String(shiftTime.start.minute).padStart(2, "0");

  // Create a Date representing the start time in workplace timezone
  const startDate = zonedTimeToUtc(
    `${year}-${month}-${day}T${startHour}:${startMinute}:00`,
    timezone
  );

  // Add duration to get end time
  const durationInMinutes = shiftTime.duration.hours * 60 + shiftTime.duration.minutes;
  const endDate = addMinutes(startDate, durationInMinutes);

  return {
    start: startDate,
    end: endDate,
  };
}

/**
 * Hook for loading default shift times for a facility/qualification/slot combination.
 *
 * Returns the default start and end times as Date objects based on the selected date.
 * Falls back to facility defaults if no qualification-specific times are configured.
 */
export function useDefaultShiftTimes({
  workplaceId,
  qualification,
  timeSlot,
  selectedDate,
  timezone,
  enabled = true,
}: UseDefaultShiftTimesOptions): UseDefaultShiftTimesReturn {
  const {
    data: shiftTimesData,
    isLoading,
    error,
    isQualificationSpecificEnabled,
  } = useGetShiftTimes({ workplaceId, qualification }, { enabled });

  const previousTimesRef = useRef<DefaultTimes | undefined>(undefined);

  const defaultTimes = useMemo(() => {
    if (!shiftTimesData || !timezone) {
      return undefined;
    }

    const slotTimes = shiftTimesData[timeSlot];
    if (!slotTimes) {
      return undefined;
    }

    const computed = convertToDateTimes(slotTimes, selectedDate, timezone);

    // Return the previous reference if the actual values haven't changed,
    // so downstream useEffects don't re-fire on query refetches with same data
    const previous = previousTimesRef.current;
    if (
      previous &&
      previous.start.getTime() === computed.start.getTime() &&
      previous.end.getTime() === computed.end.getTime()
    ) {
      return previous;
    }

    previousTimesRef.current = computed;
    return computed;
  }, [shiftTimesData, timeSlot, selectedDate, timezone]);

  return {
    defaultTimes,
    isLoading,
    error: error as Error | undefined,
    isQualificationSpecificEnabled,
  };
}
